import { importExam } from '../../persistence/importExam';
import type { Store } from '../../persistence/port';
import type { ServerClient } from '../client';
import { ClassicPruefungUnlesbar, zuBundle, type MediumZumHolen } from './abbildung';
import { holeMedien } from './medien';
import type { ClassicOsce } from './typen';

/**
 * Eine Prüfung vom Classic-Server holen — `GET /rest/osces/{id}` — und ablegen.
 *
 * Der Classic-Client macht dafür **einen** Aufruf; einen Teil- oder Sammelabruf gibt es nicht.
 *
 * ⚠️ **Unbekannte Kennung antwortet mit Status 200 und `{}`**, nicht mit `404`. Wer nur auf den
 * Statuscode sieht, legt eine leere Prüfung an. `zuBundle` fängt das ab.
 */

/** Aus unserer Kennung `classic:osce:7` wieder die `7`, die in die URL gehört. */
export function roheOsceKennung(examId: string): string {
  return examId.startsWith('classic:osce:') ? examId.slice('classic:osce:'.length) : examId;
}

export interface DownloadErgebnis {
  examId: string;
  /**
   * Die Mediendateien, die zu dieser Prüfung gehören. Die **Sätze** liegen nach dem Download in
   * der Tabelle `Media`; die **Dateien** holt Etappe 1 noch nicht, s. unten.
   */
  medien: MediumZumHolen[];
}

/**
 * **Die Mediendateien werden mitgeholt**, sobald ein Weg zum Server übergeben wird (`hole`).
 *
 * - Die Bilder, die im Prüfungstext stehen, sind **schon da** — der Classic-Server ersetzt beim
 *   Import `<img data-identifier=…>` durch das Bild selbst als `data:`-URL im HTML der Station
 *   (Protokollpapier P15). Für sie gibt es nichts zu laden.
 * - Was unter `media[]` hängt, sind zusätzliche Anhänge. Sie erscheinen in der Vignette der
 *   Station (Entscheidung des Nutzers, 2026-09-13) und werden **beim Download alle auf
 *   einmal** geholt: danach ist die Prüfung vollständig offline.
 * - ⚠️ Scheitert ein Anhang, läuft der Download **weiter** (`medien.ts`). Die Prüfung selbst
 *   steht zu dem Zeitpunkt bereits in der Ablage.
 */
export async function ladeClassicExam(
  client: ServerClient,
  store: Store,
  examId: string,
  jetzt: Date = new Date(),
  /**
   * Wie eine Mediendatei geholt wird — `medienHoler(zugang)` auf dem Gerät, etwas Gefälschtes
   * im Test. Fehlt es, bleiben die Sätze in `Media` stehen und die Dateien aus.
   */
  hole?: (id: number) => Promise<string>,
): Promise<DownloadErgebnis> {
  const roh = await client.get<ClassicOsce>('/rest/osces/' + encodeURIComponent(roheOsceKennung(examId)));

  if (roh === null || typeof roh !== 'object') {
    throw new ClassicPruefungUnlesbar('Der Classic-Server hat auf die Prüfungsanfrage keine Daten geliefert.');
  }

  const { bundle, medien } = zuBundle(roh, jetzt);

  // Erst wenn alles beisammen ist, wird geschrieben — `importExam` läuft in einer Transaktion.
  // Bräche die Abbildung mittendrin ab, stünde sonst eine halbe Prüfung in der Ablage.
  await importExam(store, bundle);

  // Erst nach dem Import: die Dateien hängen an Sätzen, die dann schon stehen — und ein
  // Abbruch beim Holen lässt so eine vollständige Prüfung zurück, nur ohne Anhänge.
  if (hole && medien.length > 0) {
    await holeMedien(medien, { examId: bundle.exam.id, hole });
  }

  return { examId: bundle.exam.id, medien };
}
